import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Apple, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * ApplePayDomainCard — verifies that the Apple Pay domain-association file
 * served on the live domain is BYTE-IDENTICAL to PayPal's official file for
 * this environment. If it matches, the operator registers the domain in the
 * PayPal dashboard (first-party merchants can't register via the API).
 */
export const ApplePayDomainCard = () => {
    const [domain, setDomain] = useState('betohalics.com');
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);

    const verify = useCallback(async (d) => {
        setChecking(true);
        setResult(null);
        try {
            const { data } = await axios.get(
                `${API}/payments/paypal/apple-pay/verify-file`,
                { params: { domain: d }, withCredentials: true }
            );
            setResult(data);
            if (data.match) toast.success('Domain file matches PayPal — ready to register!');
            else toast.error('Domain file does NOT match PayPal yet');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Verification failed');
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => { verify('betohalics.com'); }, [verify]);

    const env = result?.env;
    const match = result?.match;

    return (
        <div className="card-dark p-4 space-y-3" data-testid="apple-pay-domain-card">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <div
                        className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.03))',
                            border: '1px solid rgba(255,255,255,0.25)',
                        }}
                    >
                        <Apple className="w-4 h-4 text-white" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[13px] font-black text-white leading-tight">
                            Apple Pay — Domain File Check
                        </div>
                        <div className="text-[11px] text-[#A0AAB2] mt-0.5">
                            Confirms your file matches PayPal before you register
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {env && (
                        <span
                            className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded"
                            style={{
                                background: env === 'live' ? 'rgba(57,255,20,0.12)' : 'rgba(255,215,0,0.12)',
                                color: env === 'live' ? '#39FF14' : '#FFD700',
                                border: `1px solid ${env === 'live' ? 'rgba(57,255,20,0.4)' : 'rgba(255,215,0,0.4)'}`,
                            }}
                            data-testid="apple-pay-env-badge"
                        >
                            {env}
                        </span>
                    )}
                    <button
                        onClick={() => verify(domain)}
                        disabled={checking}
                        className="p-1.5 rounded text-[#A0AAB2] hover:text-white transition-colors"
                        data-testid="apple-pay-refresh"
                        aria-label="Re-check domain file"
                    >
                        <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="space-y-1.5">
                <Label className="text-white text-xs">Domain</Label>
                <div className="flex gap-2">
                    <Input
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="betohalics.com"
                        className="bg-black/30 border-white/15 text-white"
                        data-testid="apple-pay-domain-input"
                    />
                    <Button
                        onClick={() => verify(domain)}
                        disabled={checking || !domain.trim()}
                        className="font-black shrink-0"
                        style={{ background: 'linear-gradient(180deg, #ffffff 0%, #d8d8d8 100%)', color: '#111' }}
                        data-testid="apple-pay-verify-btn"
                    >
                        {checking ? 'Checking…' : 'Check File'}
                    </Button>
                </div>
                <p className="text-[10px] text-[#A0AAB2]">No https:// or www — just the bare domain.</p>
            </div>

            {result && (
                <div
                    className="rounded-lg p-3 space-y-2"
                    data-testid="apple-pay-verify-result"
                    style={{
                        background: match ? 'rgba(57,255,20,0.06)' : 'rgba(255,60,60,0.06)',
                        border: `1px solid ${match ? 'rgba(57,255,20,0.4)' : 'rgba(255,60,60,0.4)'}`,
                    }}
                >
                    <div className="flex items-center gap-2">
                        {match
                            ? <CheckCircle2 className="w-5 h-5 text-[#39FF14] shrink-0" />
                            : <XCircle className="w-5 h-5 text-[#ff5c5c] shrink-0" />}
                        <span className={`text-[13px] font-black ${match ? 'text-[#39FF14]' : 'text-[#ff5c5c]'}`}>
                            {match ? 'File matches PayPal — ready to register' : 'File does NOT match PayPal'}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-[#E0E0E0]">
                        <div>PayPal file: <span className="font-mono">{result.official_size ?? '—'} bytes</span></div>
                        <div>Your file: <span className="font-mono">{result.served_size ?? '—'} bytes</span></div>
                        <div>HTTP: <span className="font-mono">{result.served_status ?? '—'}</span></div>
                        <div>Type: <span className="font-mono">{(result.served_content_type || '—').split(';')[0]}</span></div>
                    </div>
                    {result.served_redirected && (
                        <div className="text-[11px] text-[#ffd0d0] flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> File is behind a redirect — Apple rejects redirects.
                        </div>
                    )}
                </div>
            )}

            {match && (
                <div className="rounded-lg p-2 text-[11px]"
                     style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.2)' }}>
                    <div className="text-white font-bold mb-1">Now register in PayPal:</div>
                    <div className="text-[#A0AAB2] leading-relaxed">
                        Developer Dashboard → <b>Live</b> → Apps &amp; Credentials → your app → Features →
                        Advanced Credit &amp; Debit Card Payments → Apple Pay <b>Manage</b> →
                        <b> Add Domain</b> → <span className="font-mono">{result.domain}</span> → <b>Register Domain</b>.
                    </div>
                </div>
            )}

            {result && !match && env && env !== 'live' && (
                <div className="rounded-lg p-2 flex items-start gap-2 text-[11px]"
                     style={{ background: 'rgba(255,215,0,0.06)', border: '1px dashed rgba(255,215,0,0.35)' }}>
                    <AlertTriangle className="w-3.5 h-3.5 text-[#FFD700] shrink-0 mt-0.5" />
                    <span className="text-[#E0E0E0]">
                        This is the <b>sandbox</b> preview. On preview the served file is your live file, so a mismatch here is expected — run this check on your <b>published production</b> site.
                    </span>
                </div>
            )}
        </div>
    );
};

export default ApplePayDomainCard;
